import { Accessor, createRenderEffect, Setter, Signal } from "solid-js";

export interface IDragHandler {
    name: string;
    handle?: HTMLElement;

    start(ev: DragEvent): void;
    stop(ev: DragEvent): void;
}

export interface IDropHandler {
    name: string;

    drop(ev: DragEvent, dragHandler: IDragHandler): void;
    enter(ev: DragEvent, dragHandler: IDragHandler): void;
    leave(ev: DragEvent, dragHandler: IDragHandler): void;
    over(ev: DragEvent, dragHandler: IDragHandler): void;
}

// Prepare the draggable directive
declare module 'solid-js' {
    namespace JSX {
        interface Directives {
            draggable: IDragHandler;
            droppable: IDropHandler;
        }
    }
}

declare global {
    interface Window {
        draggable: (options?: IDragHandler) => void;
        droppable: (options?: IDropHandler) => void;
    }
}

let draggingEl: HTMLElement;
let dragHandler: IDragHandler | undefined;
let dropHandler: IDropHandler | undefined;

window.addEventListener('dragover', ev => ev.preventDefault());
window.draggable = window.draggable ?? function (el: HTMLElement, accessor: () => IDragHandler | undefined) {
    const handler = accessor();
    let canDrag = true;

    if (!handler) return;
    el.addEventListener('mousedown', (ev: MouseEvent) => {
        canDrag = !handler.handle || ev.target === handler.handle;
    });

    el.setAttribute('draggable', 'true');
    el.addEventListener('dragstart', function (this: HTMLElement, ev: DragEvent) {
        if (!canDrag) {
            ev.preventDefault();
            return;
        }

        dragHandler = handler;
        draggingEl = el;

        ev.dataTransfer!.setDragImage(new Image(), 0, 0);
        handler.start(ev);
    });

    el.addEventListener('dragend', function (this: HTMLElement, ev: DragEvent) {
        handler.stop(ev);

        dragHandler = undefined;
        dropHandler = undefined;
    });
}

window.droppable = window.droppable ?? function (el: HTMLElement, accessor: () => IDropHandler) {
    const handler = accessor();

    function canDropFrom(dragHandler: IDragHandler | undefined) {
        return draggingEl !== el
            && ((!handler.name && !dragHandler?.name)
            || !!handler.name.split(',').find(n => n === dragHandler?.name));
    }

    el.addEventListener('dragenter', function (this: HTMLElement, ev: DragEvent) {
        ev.preventDefault();
        ev.stopPropagation();

        if (dropHandler !== handler) {
            dropHandler = handler;
            if (canDropFrom(dragHandler)) {
                handler.enter(ev, dragHandler!);
            }
        }
    });

    el.addEventListener('dragleave', function (this: HTMLElement, ev: DragEvent) {
        ev.stopPropagation();
        const relatedTarget = ev.relatedTarget as Node;

        if (relatedTarget !== el && !el.contains(relatedTarget)) {
            if (canDropFrom(dragHandler)) {
                handler.leave(ev, dragHandler!);
            }
            dropHandler = undefined;
        }
    });

    el.addEventListener('dragover', function (this: HTMLElement, ev: DragEvent) {
        ev.preventDefault();

        const dt = ev.dataTransfer;
        if (!dt) return;

        dropHandler = handler;
        if (canDropFrom(dragHandler)) {
            handler.over(ev, dragHandler!);
        }

        dt.dropEffect = "move";
    });

    el.addEventListener('drop', function (this: HTMLElement, ev: DragEvent) {
        ev.preventDefault();
        ev.stopPropagation();

        if (canDropFrom(dragHandler)) {
            handler.drop(ev, dragHandler!);
        }
        dropHandler = undefined;
    });
}

